import { useCallback, useEffect, useMemo, useState } from "react";
import { CheckCircle2, ListChecks, Timer, XCircle, Loader2, Eye, Check, X, CheckCheck, Download, ChevronDown, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Layout } from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { isAdmin } from "@/lib/roles";
import { StatCard } from "@/components/StatCard";
import { StatusBadge } from "@/components/StatusBadge";
import { MonthStatusCard } from "@/components/MonthStatusCard";
import { ApprovalFilters } from "@/components/approvals/ApprovalFilters";
import { usePersistedState } from "@/lib/usePersistedState";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { formatDate, fmtHrs, weekLabel, prettyDate, dayLabel, groupByMonth, sheetMonths, monthLabel } from "@/lib/timesheet";

const SUMMARY_ROWS = [
  ["standard", "Standard"],
  ["overtime", "Overtime"],
  ["leave", "Leave"],
];

export default function ManagerDashboard() {
  const { user } = useAuth();
  const [sheets, setSheets] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("submitted");
  const [search, setSearch] = useState("");
  const [month, setMonth] = useState("all");
  const [listOpen, setListOpen] = usePersistedState("approvals:listOpen", true);
  const [closedMonths, setClosedMonths] = usePersistedState("approvals:closedMonths", []);
  const toggleMonth = (ym) => setClosedMonths((c) => (c.includes(ym) ? c.filter((x) => x !== ym) : [...c, ym]));
  const [selected, setSelected] = useState(null);
  const [comment, setComment] = useState("");
  const [acting, setActing] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkApproving, setBulkApproving] = useState(false);
  const [exportStart, setExportStart] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1).toLocaleDateString("en-CA");
  });
  const [exportEnd, setExportEnd] = useState(() => new Date().toLocaleDateString("en-CA"));
  const [exporting, setExporting] = useState(false);

  const downloadCsv = async () => {
    if (!exportStart || !exportEnd) {
      toast.error("Pick a date range to export");
      return;
    }
    if (exportEnd < exportStart) {
      toast.error("End date must be on or after the start date");
      return;
    }
    setExporting(true);
    try {
      const res = await api.get(`/admin/export/monthly.csv?start=${exportStart}&end=${exportEnd}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `mokora-timesheets-${exportStart}_to_${exportEnd}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not download export");
    } finally {
      setExporting(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([api.get("/timesheets"), api.get("/timesheets/stats")]);
      setSheets(a.data);
      setStats(b.data);
      setSelectedIds([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const months = useMemo(() => [...new Set(sheets.flatMap(sheetMonths))].sort().reverse(), [sheets]);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return sheets.filter((s) => (filter === "all" || s.status === filter)
      && (!q || (s.employee_name || "").toLowerCase().includes(q))
      && (month === "all" || sheetMonths(s).includes(month)));
  }, [sheets, filter, search, month]);
  const groups = useMemo(() => groupByMonth(filtered), [filtered]);
  const pickTile = (status) => {
    setFilter(status); setSelectedIds([]); setListOpen(true);
    setTimeout(() => document.querySelector("[data-testid=manager-dashboard-view]")?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };
  const pendingInView = useMemo(() => filtered.filter((s) => s.status === "submitted"), [filtered]);
  const allPendingSelected = pendingInView.length > 0 && selectedIds.length === pendingInView.length;

  const toggleOne = (id) => setSelectedIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));
  const toggleAll = () => setSelectedIds(allPendingSelected ? [] : pendingInView.map((s) => s.id));

  const bulkApprove = async () => {
    if (selectedIds.length === 0) return;
    setBulkApproving(true);
    try {
      const { data } = await api.post("/timesheets/bulk-approve", { ids: selectedIds });
      toast.success(`Approved ${data.approved} timesheet${data.approved === 1 ? "" : "s"}`);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBulkApproving(false);
    }
  };

  const openDetail = (ts) => {
    setSelected(ts);
    setComment("");
  };

  const act = async (kind) => {
    if (!selected) return;
    if (kind === "reject" && !comment.trim()) {
      toast.error("A comment is required to reject");
      return;
    }
    setActing(true);
    try {
      await api.post(`/timesheets/${selected.id}/${kind}`, { comment });
      toast.success(kind === "approve" ? "Timesheet approved" : "Timesheet rejected");
      setSelected(null);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setActing(false);
    }
  };

  const sum = selected?.summary || {};

  return (
    <Layout>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight sm:text-4xl">Approval Queue</h1>
          <p className="mt-1 text-muted-foreground">Review and approve submitted timesheets.</p>
        </div>
        {isAdmin(user) && (
        <div className="flex flex-wrap items-end gap-2" data-testid="range-export">
          <div className="space-y-1.5">
            <Label htmlFor="export-start" className="text-xs text-muted-foreground">From</Label>
            <Input id="export-start" data-testid="export-start-input" type="date" value={exportStart} onChange={(e) => setExportStart(e.target.value)} className="w-[9.5rem]" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="export-end" className="text-xs text-muted-foreground">To</Label>
            <Input id="export-end" data-testid="export-end-input" type="date" value={exportEnd} onChange={(e) => setExportEnd(e.target.value)} className="w-[9.5rem]" />
          </div>
          <Button data-testid="export-csv-btn" variant="outline" className="gap-2" disabled={exporting} onClick={downloadCsv}>
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            Download CSV
          </Button>
        </div>
        )}
      </div>

      <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard testId="tile-pending" onClick={() => pickTile("submitted")} active={filter === "submitted"} title="Pending Review" metric={stats ? stats.submitted : "-"} sub="Awaiting your action" icon={ListChecks} accent="bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300" />
        <StatCard testId="tile-pending-hours" onClick={() => pickTile("submitted")} active={filter === "submitted"} title="Pending Hours" metric={stats ? fmtHrs(stats.pending_hours) : "-"} sub="To be approved" icon={Timer} accent="bg-blue-100 text-blue-700 dark:bg-blue-950/50 dark:text-blue-300" />
        <StatCard testId="tile-approved" onClick={() => pickTile("approved")} active={filter === "approved"} title="Approved" metric={stats ? stats.approved : "-"} sub="Signed off" icon={CheckCircle2} accent="bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300" />
        <StatCard testId="tile-rejected" onClick={() => pickTile("rejected")} active={filter === "rejected"} title="Rejected" metric={stats ? stats.rejected : "-"} sub="Sent back" icon={XCircle} accent="bg-rose-100 text-rose-700 dark:bg-rose-950/50 dark:text-rose-300" />
      </div>

      <MonthStatusCard refreshKey={sheets} />

      <Card className="mt-6 scroll-mt-28 overflow-hidden" data-testid="manager-dashboard-view">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-4">
          <button type="button" data-testid="timesheets-toggle" onClick={() => setListOpen((o) => !o)} className="flex items-center gap-2 text-left" aria-expanded={listOpen}>
            <ChevronDown className={`h-5 w-5 transition-transform ${listOpen ? "" : "-rotate-90"}`} />
            <h2 className="font-heading text-lg font-bold tracking-tight">Timesheets</h2>
            <span data-testid="timesheets-month-label" className="inline-flex items-center gap-1.5 rounded-full border border-[#022a51]/20 bg-[#022a51]/5 px-2.5 py-0.5 text-xs font-semibold text-[#022a51] dark:border-sky-400/30 dark:bg-sky-400/10 dark:text-sky-300">
              <CalendarDays className="h-3.5 w-3.5" /> {month === "all" ? "All months" : monthLabel(month)}
            </span>
          </button>
          <Tabs value={filter} onValueChange={(v) => { setFilter(v); setSelectedIds([]); }}>
            <TabsList data-testid="approval-filter-tabs">
              <TabsTrigger value="submitted">Pending</TabsTrigger>
              <TabsTrigger value="approved">Approved</TabsTrigger>
              <TabsTrigger value="rejected">Rejected</TabsTrigger>
              <TabsTrigger value="all">All</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
        {listOpen && <>
        <ApprovalFilters search={search} onSearch={setSearch} month={month} onMonth={setMonth} months={months} />

        {selectedIds.length > 0 && (
          <div data-testid="bulk-action-bar" className="flex flex-wrap items-center justify-between gap-3 border-b border-border bg-[hsl(var(--accent))]/10 px-5 py-3">
            <span className="text-sm font-medium"><span className="font-mono font-bold">{selectedIds.length}</span> selected</span>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={() => setSelectedIds([])}>Clear</Button>
              <Button data-testid="bulk-approve-btn" size="sm" className="gap-2 bg-emerald-600 text-white hover:bg-emerald-700" disabled={bulkApproving} onClick={bulkApprove}>
                {bulkApproving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCheck className="h-4 w-4" />} Approve Selected
              </Button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-16 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center text-muted-foreground">Nothing here right now.</div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    {pendingInView.length > 0 && (
                      <Checkbox data-testid="bulk-select-all" checked={allPendingSelected} onCheckedChange={toggleAll} aria-label="Select all pending" />
                    )}
                  </TableHead>
                  <TableHead>Employee</TableHead>
                  <TableHead>Week</TableHead>
                  <TableHead className="text-right">Worked</TableHead>
                  <TableHead className="text-right">Leave</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {groups.map((g) => [
                  <TableRow key={`g-${g.ym}`} data-testid={`approval-month-group-${g.ym}`} className="bg-secondary/40 hover:bg-secondary/40">
                    <TableCell colSpan={7} className="py-2 font-heading text-sm font-bold">
                      <button type="button" data-testid={`approval-month-toggle-${g.ym}`} onClick={() => toggleMonth(g.ym)} className="flex w-full items-center gap-2 text-left">
                        <ChevronDown className={`h-4 w-4 transition-transform ${closedMonths.includes(g.ym) ? "-rotate-90" : ""}`} />
                        {g.label} <span className="ml-1 font-mono text-xs font-normal text-muted-foreground">{g.items.length} week{g.items.length === 1 ? "" : "s"}</span>
                      </button>
                    </TableCell>
                  </TableRow>,
                  ...(closedMonths.includes(g.ym) ? [] : g.items).map((ts) => (
                  <TableRow key={`${g.ym}-${ts.id}`} data-testid={ts.week_ending.startsWith(g.ym) ? `manager-pending-row-${ts.id}` : `manager-pending-row-${ts.id}-${g.ym}`}>
                    <TableCell>
                      {ts.status === "submitted" && (
                        <Checkbox data-testid={`bulk-select-${ts.id}${ts.week_ending.startsWith(g.ym) ? "" : `-${g.ym}`}`} checked={selectedIds.includes(ts.id)} onCheckedChange={() => toggleOne(ts.id)} aria-label="Select timesheet" />
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">{ts.employee_name}</div>
                      <div className="text-xs text-muted-foreground">{ts.role_team}</div>
                    </TableCell>
                    <TableCell className="font-medium">{weekLabel(ts.week_ending)}</TableCell>
                    <TableCell className="text-right font-mono font-semibold tabular">{fmtHrs(ts.summary?.total_worked || 0)}</TableCell>
                    <TableCell className="text-right font-mono tabular text-muted-foreground">{fmtHrs(ts.summary?.total_leave || 0)}</TableCell>
                    <TableCell><StatusBadge status={ts.status} /></TableCell>
                    <TableCell className="text-right">
                      <Button data-testid={`manager-inspect-btn-${ts.id}${ts.week_ending.startsWith(g.ym) ? "" : `-${g.ym}`}`} variant="ghost" size="sm" className="gap-1.5" onClick={() => openDetail(ts)}>
                        <Eye className="h-4 w-4" /> Review
                      </Button>
                    </TableCell>
                  </TableRow>
                  )),
                ])}
              </TableBody>
            </Table>
          </div>
        )}
        </>}
      </Card>

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-2xl">
          {selected && (
            <>
              <SheetHeader>
                <SheetTitle className="font-heading text-2xl font-extrabold tracking-tight">{selected.employee_name}</SheetTitle>
                <SheetDescription>
                  {selected.role_team} · {weekLabel(selected.week_ending)}
                </SheetDescription>
              </SheetHeader>

              <div className="mt-4 flex items-center gap-3">
                <StatusBadge status={selected.status} />
                <span className="font-mono text-sm font-semibold tabular">Worked: {fmtHrs(sum.total_worked || 0)} · Leave: {fmtHrs(sum.total_leave || 0)}</span>
              </div>

              <div className="mt-5 grid grid-cols-3 gap-2 sm:grid-cols-5">
                {SUMMARY_ROWS.map(([key, label]) => (
                  <div key={key} className="rounded-lg border border-border bg-secondary/30 p-2 text-center">
                    <div className="font-mono text-[9px] uppercase tracking-wide text-muted-foreground">{label}</div>
                    <div className="font-mono text-sm font-bold tabular">{fmtHrs(sum[key] || 0)}</div>
                  </div>
                ))}
              </div>

              <div className="mt-5 overflow-x-auto rounded-xl border border-border">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="border-b border-border bg-secondary/40 text-left">
                      <th className="px-3 py-2 text-xs font-semibold uppercase text-muted-foreground">Date</th>
                      <th className="px-2 py-2 text-xs font-semibold uppercase text-muted-foreground">Type</th>
                      <th className="px-2 py-2 text-xs font-semibold uppercase text-muted-foreground">Client</th>
                      <th className="px-2 py-2 text-xs font-semibold uppercase text-muted-foreground">Project</th>
                      <th className="px-2 py-2 text-xs font-semibold uppercase text-muted-foreground">Description</th>
                      <th className="px-2 py-2 text-center text-xs font-semibold uppercase text-muted-foreground">Hrs</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selected.entries.map((e, i) => (
                      <tr key={`${e.date}-${e.type}-${i}`} className="border-b border-border last:border-0">
                        <td className="px-3 py-2 font-mono whitespace-nowrap">{prettyDate(e.date)} <span className="text-muted-foreground">{dayLabel(e.date)}</span></td>
                        <td className="px-2 py-2">{e.type}</td>
                        <td className="px-2 py-2 text-muted-foreground">{e.client || "-"}</td>
                        <td className="px-2 py-2 text-muted-foreground">{e.project || "-"}</td>
                        <td className="px-2 py-2 text-muted-foreground">{e.notes || "-"}</td>
                        <td className="px-2 py-2 text-center font-mono tabular">{fmtHrs(e.hours)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {selected.declaration_name && (
                <p className="mt-3 text-xs text-muted-foreground">
                  Declared a true record by <span className="font-medium text-foreground">{selected.declaration_name}</span>
                  {selected.submitted_at && <> on {formatDate(selected.submitted_at)}</>}.
                </p>
              )}

              {selected.manager_comment && selected.status !== "submitted" && (
                <div className="mt-4 rounded-lg border border-border bg-secondary/40 px-4 py-3 text-sm">
                  <div className="font-semibold">For Mokora use only - Comments</div>
                  <div className="mt-1 text-muted-foreground">{selected.manager_comment}</div>
                </div>
              )}

              {selected.status === "submitted" && (
                <div className="mt-6 space-y-3">
                  <div className="space-y-2">
                    <Label htmlFor="mgr-comment">Comments (required to reject)</Label>
                    <Textarea id="mgr-comment" data-testid="manager-comment-input" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Optional note for approval, or reason for rejection…" rows={3} />
                  </div>
                  <div className="flex gap-3">
                    <Button data-testid={`manager-reject-btn-${selected.id}`} variant="outline" className="flex-1 gap-2 border-rose-300 text-rose-700 hover:bg-rose-50 dark:border-rose-800 dark:text-rose-300 dark:hover:bg-rose-950/40" disabled={acting} onClick={() => act("reject")}>
                      <X className="h-4 w-4" /> Reject
                    </Button>
                    <Button data-testid={`manager-approve-btn-${selected.id}`} className="flex-1 gap-2 bg-emerald-600 text-white hover:bg-emerald-700" disabled={acting} onClick={() => act("approve")}>
                      {acting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Approve
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </SheetContent>
      </Sheet>
    </Layout>
  );
}
